import { useMemo, useState } from 'react';

/** Personentyp-Code (Stammdaten) → Typ der Person in der Liste. */
export const TYPE_ALIASES: Record<string, string> = {
  kunde: 'customer',
  kunden: 'customer',
  lehrer: 'teacher',
  lehrkraft: 'teacher',
  schueler: 'subprofile',
  schüler: 'subprofile',
};

const joinTime = (value: string | null | undefined): number => {
  const time = value ? new Date(value).getTime() : NaN;
  return Number.isFinite(time) ? time : 0;
};

export type PersonFilterSort = 'name_asc' | 'name_desc' | 'newest_join' | 'oldest_join';

type PersonSelectionItem = {
  id: string;
  name: string;
  email?: string | null;
  type: string;
  status: string;
  joinDate: string;
  parentId?: string | null;
};

type Params<T extends PersonSelectionItem> = {
  people: T[];
  searchTerm: string;
  activeType: string;
  statusFilter: string;
  sortBy: PersonFilterSort;
  page: number;
  pageSize: number;
  allowedIds?: Set<string>;
};

export function usePersonFilterSelection<T extends PersonSelectionItem>({
  people,
  searchTerm,
  activeType,
  statusFilter,
  sortBy,
  page,
  pageSize,
  allowedIds,
}: Params<T>) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filteredPeople = useMemo(() => {
    const filtered = people.filter((person) => {
      if (person.parentId) return false;
      if (allowedIds && !allowedIds.has(person.id)) return false;

      const matchesSearch =
        normalizedSearch.length === 0 ||
        person.name.toLowerCase().includes(normalizedSearch) ||
        String(person.email || '').toLowerCase().includes(normalizedSearch);

      // Personentypen heißen im Stammdatenkatalog „kunde“/„lehrer“, die Zeilen
      // tragen „customer“/„teacher“ — ohne Abgleich traf der Typfilter nie (T-0080).
      const matchesType = activeType === 'all' || person.type === (TYPE_ALIASES[activeType] ?? activeType);
      const matchesStatus = statusFilter === 'all' || person.status === statusFilter;
      return matchesSearch && matchesType && matchesStatus;
    });

    const sorted = [...filtered];
    sorted.sort((a, b) => {
      if (sortBy === 'name_desc') return b.name.localeCompare(a.name, 'de');
      // Ohne Datum gilt ein Datensatz als ältester (leeres Datum ergäbe NaN, T-0080).
      if (sortBy === 'newest_join') return joinTime(b.joinDate) - joinTime(a.joinDate);
      if (sortBy === 'oldest_join') return joinTime(a.joinDate) - joinTime(b.joinDate);
      return a.name.localeCompare(b.name, 'de');
    });
    return sorted;
  }, [people, normalizedSearch, activeType, statusFilter, sortBy, allowedIds]);

  const totalFiltered = filteredPeople.length;
  const pageStartIdx = page * pageSize;
  const pageEndIdx = Math.min(pageStartIdx + pageSize, totalFiltered);
  const pagedPeople = filteredPeople.slice(pageStartIdx, pageEndIdx);
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));

  const toggleSelected = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());
  const replaceSelection = (ids: Iterable<string>) => setSelectedIds(new Set(ids));

  const togglePageSelection = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const person of pagedPeople) {
        if (checked) next.add(person.id);
        else next.delete(person.id);
      }
      return next;
    });
  };

  const pageSelectionState = useMemo(() => {
    if (pagedPeople.length === 0) return { all: false, some: false };
    let selectedCount = 0;
    for (const person of pagedPeople) {
      if (selectedIds.has(person.id)) selectedCount += 1;
    }
    return {
      all: selectedCount === pagedPeople.length,
      some: selectedCount > 0 && selectedCount < pagedPeople.length,
    };
  }, [pagedPeople, selectedIds]);

  return {
    selectedIds,
    filteredPeople,
    pagedPeople,
    totalFiltered,
    pageStartIdx,
    pageEndIdx,
    totalPages,
    toggleSelected,
    clearSelection,
    replaceSelection,
    togglePageSelection,
    pageSelectionState,
  };
}
